// CoreMetrics.tsx — job-queue depth and per-customer SLA, which ARE captured.
//
// Reliability.tsx listed four things as "not captured yet". Two of those were
// wrong, and being wrong in the direction of "we have no data" is how a screen
// stays empty next to a table that holds the answer:
//
//   * JOB QUEUE. The note said depth "requires either a real queue (pg-boss/
//     BullMQ) or a jobs table the workers claim from". The second one exists:
//     extraction_jobs has a CHECK-constrained status column plus created_at,
//     started_at and completed_at.
//
//   * PER-CUSTOMER SLA. The note read "SLA" as infrastructure uptime. The
//     product models a TURNAROUND SLA — migration 059 put sla_target_date,
//     sla_severity, sla_breached and sla_breached_at on contract_requests — and
//     turnaround is the SLA an operator actually reviews with a customer.
//
// The other two remain uncaptured and are still stated as such here rather than
// filled in with something that looks like an answer:
//
//   * HTTP availability and request p50/p95 — nothing times requests. Process
//     uptime is NOT availability; it is how long this container has been up,
//     which says nothing about whether it was answering. It is labelled process
//     uptime for that reason and is never rendered as a percentage.
//   * Infrastructure incidents — no incidents table, no status-page feed. SLA
//     breaches are product events, not outages, and showing them under an
//     "incidents" heading would misrepresent both.

import React, { useCallback, useEffect, useState } from "react";
import { PT, fmtNum } from "../../platformTheme";
import { pget, perr } from "./api";
import { PCard, PSection, PBtn, PBadge, PBanner, PEmpty, PSpinner, PTable, PRow, PStat, PGrid, th, td, tdNum } from "./ui";

interface CoreRow {
  tenant_id: string | null;
  tenant_name: string;
  queue: {
    pending: number; processing: number;
    failed_in_window: number; completed_in_window: number;
    oldest_pending_seconds: string | number | null;
  };
  sla: { breached_total: number; breached_in_window: number; at_risk: number; open_with_target: number };
  timeline: { sla_breached_at: string; sla_severity: string | null; sla_target_date: string | null }[];
}

/** Seconds → the coarsest unit that is still honest. */
const age = (s: string | number | null | undefined): string => {
  const n = Number(s);
  if (!Number.isFinite(n) || n <= 0) return "—";
  if (n < 90) return `${Math.round(n)}s`;
  if (n < 5400) return `${Math.round(n / 60)}m`;
  if (n < 172800) return `${Math.round(n / 3600)}h`;
  return `${Math.round(n / 86400)}d`;
};

const severityTone = (s: string | null): "err" | "warn" | "neutral" =>
  s === "critical" ? "err" : s === "high" ? "warn" : "neutral";

export const CoreMetrics: React.FC<{ days: number }> = ({ days }) => {
  const [rows, setRows] = useState<CoreRow[] | null>(null);
  const [failed, setFailed] = useState<{ tenant_name: string }[]>([]);
  const [uptime, setUptime] = useState<{ formatted?: string } | null>(null);
  const [notes, setNotes] = useState<{ availability?: string; incidents?: string }>({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [at, setAt] = useState<Date | null>(null);

  const load = useCallback(async () => {
    setErr(""); setBusy(true);
    const r = await pget("/metrics/core", { days });
    setBusy(false); setAt(new Date());
    if (!r.ok) { setErr(perr(r, "Could not load core metrics")); setRows([]); return; }
    setRows(r.data?.tenants || []);
    setFailed(r.data?.tenants_failed || []);
    setUptime(r.data?.process_uptime || null);
    setNotes({ availability: r.data?.availability_note, incidents: r.data?.incidents_note });
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const t = (rows || []).reduce((a, r) => ({
    pending: a.pending + (r.queue?.pending || 0),
    processing: a.processing + (r.queue?.processing || 0),
    failed: a.failed + (r.queue?.failed_in_window || 0),
    breached: a.breached + (r.sla?.breached_in_window || 0),
    atRisk: a.atRisk + (r.sla?.at_risk || 0),
    oldest: Math.max(a.oldest, Number(r.queue?.oldest_pending_seconds) || 0),
  }), { pending: 0, processing: 0, failed: 0, breached: 0, atRisk: 0, oldest: 0 });

  const timeline = (rows || [])
    .flatMap((r) => (r.timeline || []).map((e) => ({ ...e, tenant_name: r.tenant_name })))
    .sort((a, b) => (a.sla_breached_at < b.sla_breached_at ? 1 : -1))
    .slice(0, 40);

  return (
    <>
      <PBanner>{err}</PBanner>
      {failed.length > 0 && (
        <PBanner>
          {failed.length} customer{failed.length > 1 ? "s" : ""} could not be read
          ({failed.map((f) => f.tenant_name).join(", ")}) — the figures below are partial.
        </PBanner>
      )}

      <PSection
        title="Document processing queue"
        sub={`extraction_jobs · every customer${at ? ` · updated ${at.toLocaleTimeString()}` : ""}`}
        right={<PBtn small disabled={busy} onClick={load}>{busy ? "Refreshing…" : "Refresh"}</PBtn>}
      >
        <PGrid min={175}>
          <PStat label="Waiting" value={rows === null ? "…" : fmtNum(t.pending)} sub="queued, not started" />
          <PStat label="Running" value={rows === null ? "…" : fmtNum(t.processing)} sub="in progress now" />
          <PStat
            label="Oldest waiting" value={rows === null ? "…" : age(t.oldest)}
            // Depth alone cannot distinguish a queue of 3 that is four minutes
            // old from a queue of 3 that is four days old.
            sub={t.pending ? "age of the longest-queued job" : "nothing waiting"}
          />
          <PStat
            label={`Failed · ${days}d`} value={rows === null ? "…" : fmtNum(t.failed)}
            sub={t.failed > 0 ? "jobs that ended in failure" : "no failures"}
          />
        </PGrid>

        <div style={{ marginTop: 14 }}>
          <PCard style={{ padding: 0 }}>
            {rows === null ? <PSpinner /> : rows.length === 0 ? <PEmpty>No customers to report on.</PEmpty> : (
              <PTable
                minWidth={820}
                head={<>
                  <th style={th}>Customer</th>
                  <th style={{ ...th, textAlign: "right" }}>Waiting</th>
                  <th style={{ ...th, textAlign: "right" }}>Running</th>
                  <th style={{ ...th, textAlign: "right" }}>Oldest waiting</th>
                  <th style={{ ...th, textAlign: "right" }}>Failed</th>
                  <th style={{ ...th, textAlign: "right" }}>Completed</th>
                </>}
              >
                {rows.map((r) => (
                  <PRow key={(r.tenant_id || r.tenant_name) + "-q"}>
                    <td style={{ ...td, fontWeight: 600 }}>{r.tenant_name}</td>
                    <td style={tdNum}>{fmtNum(r.queue?.pending)}</td>
                    <td style={tdNum}>{fmtNum(r.queue?.processing)}</td>
                    <td style={tdNum}>{age(r.queue?.oldest_pending_seconds)}</td>
                    <td style={{ ...tdNum, color: (r.queue?.failed_in_window || 0) > 0 ? PT.errTx : PT.txD }}>
                      {fmtNum(r.queue?.failed_in_window)}
                    </td>
                    <td style={{ ...tdNum, color: PT.txM }}>{fmtNum(r.queue?.completed_in_window)}</td>
                  </PRow>
                ))}
              </PTable>
            )}
          </PCard>
        </div>
      </PSection>

      <PSection title="Customer SLA" sub="turnaround targets on contract requests (migration 059) — not an uptime SLA">
        <PGrid min={175}>
          <PStat
            label={`Breached · ${days}d`} value={rows === null ? "…" : fmtNum(t.breached)}
            sub={t.breached > 0 ? "passed their target date" : "none breached"}
          />
          <PStat
            label="At risk now" value={rows === null ? "…" : fmtNum(t.atRisk)}
            sub={t.atRisk > 0 ? "due today or overdue, still open" : "nothing overdue"}
          />
          <PStat
            label="Open with a target"
            value={rows === null ? "…" : fmtNum((rows || []).reduce((a, r) => a + (r.sla?.open_with_target || 0), 0))}
            sub="being tracked against an SLA"
          />
        </PGrid>

        <div style={{ marginTop: 14 }}>
          <PCard style={{ padding: 0 }}>
            <div style={{ padding: "12px 14px", fontSize: 13, fontWeight: 600, borderBottom: `1px solid ${PT.brdSubtle}` }}>
              Breach timeline
              <span style={{ fontWeight: 400, fontSize: 11, color: PT.txD, marginLeft: 8 }}>
                when and how severe — no matter contents are read or shown
              </span>
            </div>
            {rows === null ? <PSpinner /> : timeline.length === 0 ? (
              <PEmpty>
                No SLA breaches in the last {days} days. This is a real zero — the query counts
                rows that exist, so an empty timeline means none were recorded, not that nothing
                is measured.
              </PEmpty>
            ) : (
              <PTable
                minWidth={620}
                head={<><th style={th}>When</th><th style={th}>Customer</th><th style={th}>Severity</th><th style={th}>Target was</th></>}
              >
                {timeline.map((e, i) => (
                  <PRow key={i}>
                    <td style={td}>{new Date(e.sla_breached_at).toLocaleString()}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{e.tenant_name}</td>
                    <td style={td}><PBadge tone={severityTone(e.sla_severity)}>{e.sla_severity || "standard"}</PBadge></td>
                    <td style={{ ...td, color: PT.txM }}>{e.sla_target_date || "—"}</td>
                  </PRow>
                ))}
              </PTable>
            )}
          </PCard>
        </div>
      </PSection>

      <PSection title="Availability" sub="what this platform does and does not record">
        <PCard>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
            <span style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", color: PT.txD }}>
              API process uptime
            </span>
            <span style={{ fontSize: 15, fontWeight: 600 }}>{uptime?.formatted || "—"}</span>
            <PBadge tone="warn">not an availability figure</PBadge>
          </div>
          <div style={{ fontSize: 12.5, color: PT.txM, lineHeight: 1.55, maxWidth: 780 }}>
            {notes.availability}
          </div>
          <div style={{ fontSize: 12.5, color: PT.txM, lineHeight: 1.55, maxWidth: 780, marginTop: 10 }}>
            {notes.incidents}
          </div>
        </PCard>
      </PSection>
    </>
  );
};
